import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { useEffect, useState } from "react";
import {
  MessageCircle, FileText, Search,
  CheckCircle2, ArrowRight, Star, MapPin, Mail,
  ShieldCheck, Zap, Target, Sparkles, ChevronDown,
  Minus,
} from "lucide-react";
import heroSlide1 from "@/assets/hero-slide-1.jpg";
import heroSlide2 from "@/assets/hero-slide-2.jpg";
import heroSlide3 from "@/assets/hero-slide-3.jpg";
import heroGazeboBmw from "@/assets/hero-gazebo-bmw.png.asset.json";
import heroGazeboJetour from "@/assets/hero-gazebo-jetour.png.asset.json";
import heroDesignerVideo from "@/assets/hero-designer.mp4.asset.json";
import brandLogo from "@/assets/brandin-logo.png";
import brandInABoxKit from "@/assets/brand-in-a-box-kit.jpg";
import { HeroBanner } from "@/components/HeroBanner";
import { StickyWhatsApp, waLink, WA_DISPLAY, openWhatsApp } from "@/components/WhatsAppButton";
import { trackWhatsAppClick, trackLeadSubmit } from "@/lib/analytics";
import { useCurrency, formatPrice } from "@/lib/useCurrency";
import { useI18n } from "@/lib/useI18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useStaggerReveal } from "@/hooks/useScrollReveal";

import pEquitiLogo from "@/assets/portfolio-equiti-logo.png";
import pEquitiProfile from "@/assets/portfolio-equiti-profile.png";
import pLumexa from "@/assets/portfolio-lumexa.png";
import pKakapa from "@/assets/portfolio-kakapa.png";
import pLlmp from "@/assets/portfolio-llmp.png";
import pBatsumiLogo from "@/assets/portfolio-batsumi-logo.png";
import pEmmanuel from "@/assets/portfolio-emmanuel.png";
import pBatsumiProfile from "@/assets/portfolio-batsumi-profile.png";
import { services } from "@/data/services";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Logo Design, Branding & Websites — BrandIn Studio SA" },
      {
        name: "description",
        content:
          "Affordable logo design, branding, websites and WhatsApp Business setup for small businesses in South Africa. Johannesburg, Midrand & nationwide.",
      },
      {
        name: "keywords",
        content:
          "logo design South Africa, branding Johannesburg, website design small business, WhatsApp Business setup, company profile design, SEO South Africa, branding agency Midrand",
      },
      { property: "og:title", content: "Logo Design, Branding & Websites — BrandIn Studio SA" },
      { property: "og:description", content: "Logo design, websites, WhatsApp Business setup and digital marketing for small businesses in South Africa." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.brandinstudio.co.za/" },
      { property: "og:image", content: "https://www.brandinstudio.co.za/og-cover.jpg" },
      { name: "twitter:image", content: "https://www.brandinstudio.co.za/og-cover.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://www.brandinstudio.co.za/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: { "@type": "Answer", text: faq.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "BrandIn Studio",
          url: "https://www.brandinstudio.co.za/",
          telephone: "+27752628060",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Waterfall Office Park, Bekker Street, Vorna Valley",
            addressLocality: "Midrand",
            postalCode: "1686",
            addressCountry: "ZA",
          },
          areaServed: "ZA",
          priceRange: "R",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          provider: { "@type": "LocalBusiness", name: "BrandIn Studio" },
          areaServed: "ZA",
          name: "Branding, Web Design & Digital Marketing",
          serviceType: [
            "Logo Design & Brand Identity",
            "Website & Landing Page Design",
            "WhatsApp Business Setup",
            "Company Profile Design",
            "Social Media Posters",
            "SEO & Digital Marketing",
          ],
        }),
      },
    ],
  }),
  component: LandingPage,
});

const nav = [
  { key: "nav.services", href: "/services" },
  { key: "nav.about", href: "#about" },
  { key: "nav.portfolio", href: "#portfolio" },
  { key: "nav.packages", href: "#packages" },
  { key: "nav.process", href: "#process" },
  { key: "nav.faq", href: "#faq" },
];





const problems = [
  { icon: ShieldCheck, title: "Weak branding reduces trust", desc: "Inconsistent logos and amateur design make customers question your credibility before they reach out." },
  { icon: Target, title: "Poor online presence loses leads", desc: "No website, weak WhatsApp profile, or scattered social pages quietly send your customers to competitors." },
  { icon: Zap, title: "No marketing system wastes opportunity", desc: "Without a clear strategy, every poster, post and ad is disconnected — and your budget disappears." },
];

type Package = {
  name: string;
  priceZar: number; // 0 = custom / Executive
  outcome: string;
  tagline: string;
  bullets: string[];
  cta: string;
  highlight?: boolean;
  isCustom?: boolean;
};

const packages: Package[] = [
  {
    name: "Starter",
    priceZar: 999,
    outcome: "Look professional from day one",
    tagline: "Solopreneurs & side-hustles",
    bullets: [
      "Custom logo with brand colours & fonts",
      "Company profile design",
    ],
    cta: "Start on WhatsApp",
  },
  {
    name: "Growth",
    priceZar: 3999,
    outcome: "Look established and win bigger clients",
    tagline: "SMEs ready to scale",
    bullets: [
      "Custom logo with brand colours & fonts",
      "Company profile design",
      "Mobile-friendly landing page",
      "Business card designs",
      "Email signature design",
    ],
    cta: "Start on WhatsApp",
    highlight: true,
  },
  {
    name: "Pro",
    priceZar: 8999,
    outcome: "Premium brand presence with digital marketing",
    tagline: "Established businesses",
    bullets: [
      "Everything in Growth",
      "SEO & Google Business setup",
      "Social media posters",
    ],
    cta: "Start on WhatsApp",
  },
];

const packageServiceDefaults: Record<string, string[]> = {
  "Starter": [
    "Logo Design & Brand Identity",
    "Business Profiles & Documents",
  ],
  "Growth": [
    "Logo Design & Brand Identity",
    "Business Profiles & Documents",
    "Landing Pages & Websites",
  ],
  "Pro": [
    "Logo Design & Brand Identity",
    "Business Profiles & Documents",
    "Landing Pages & Websites",
    "SEO & Keyword Strategy",
    "Social Media Posters",
  ],
};

const comparisonFeatures = [
  "Logo & Brand Identity",
  "Website / Landing Page",
  "Company Profile / Documents",
  "Business Card Design",
  "Email Signature",
  "Social Media Posters",
  "SEO & Google Business",
  "Marketing Campaign",
  "Support / SLA",
];

const comparisonMap: Record<string, string[]> = {
  Starter: ["check", "dash", "check", "dash", "dash", "dash", "dash", "dash", "dash"],
  Growth:  ["check", "check", "check", "check", "check", "dash", "dash", "dash", "dash"],
  Pro:     ["check", "check", "check", "check", "check", "check", "check", "dash", "dash"],
};

const portfolio: { img: string; label: string; tag: string; tone: "light" | "dark" }[] = [
  { img: pEquitiLogo, label: "Equiti Energy — Logo & Identity", tag: "Branding", tone: "dark" },
  { img: pEquitiProfile, label: "Equiti Energy — Company Profile", tag: "Profile", tone: "dark" },
  { img: pLumexa, label: "Lumexa Group — Corporate Logo", tag: "Identity", tone: "dark" },
  { img: pKakapa, label: "Kakapa Security — Vehicle Branding", tag: "Print", tone: "light" },
  { img: pLlmp, label: "LLMP Electrical & Solar — Logo Suite", tag: "Logos", tone: "light" },
  { img: pBatsumiLogo, label: "Batsumi Projects — Logo Design", tag: "Identity", tone: "light" },
  { img: pEmmanuel, label: "Emmanuel Divine Revival — Ministry Crest", tag: "Logo", tone: "light" },
  { img: pBatsumiProfile, label: "Batsumi Projects — Company Profile", tag: "Profile", tone: "light" },
];

const why = [
  "Practical business-focused design",
  "Sales-driven landing pages",
  "WhatsApp-first lead generation",
  "Professional brand consistency",
  "Fast turnaround",
  "Affordable packages for growing businesses",
];

const steps = [
  { n: "01", title: "Free Brand Assessment", desc: "We review your current brand, website and online presence." },
  { n: "02", title: "Strategy & Design Direction", desc: "We align on positioning, look, feel and conversion goals." },
  { n: "03", title: "Build Brand Assets", desc: "Logos, pages, profiles, posters and WhatsApp — done right." },
  { n: "04", title: "Launch & Start Getting Leads", desc: "Go live with a system designed to bring enquiries to WhatsApp." },
];

const testimonials = [
  { name: "Thabo M.", role: "Owner, Logistics Startup", quote: "Our enquiries doubled in the first month. The WhatsApp setup and landing page made us look established overnight." },
  { name: "Naledi K.", role: "Founder, Beauty Salon", quote: "I finally have branding I'm proud to share. Clients comment on how professional everything looks now." },
  { name: "Sipho D.", role: "Director, Security Firm", quote: "BrandIn Studio understood our market. The company profile alone has helped us win bigger tenders." },
];

const faqs = [
  {
    q: "What is branding for a small business?",
    a: "Branding is the total impression your business makes on customers — your logo, colours, fonts, messaging, website, social media and even how you answer WhatsApp. For small businesses in South Africa, strong branding tells customers you are professional, trustworthy and worth their money before they ever speak to you. It is not just a logo; it is the look, feel and experience of every touchpoint.",
  },
  {
    q: "Why does branding matter for small businesses?",
    a: "Customers judge a business in seconds. A consistent, professional brand builds instant trust, makes marketing more effective and lets you charge what you are worth. Without clear branding, even great products get overlooked because competitors look more credible. For South African small businesses, branding is the difference between being seen as a side hustle and being treated as a serious company.",
  },
  {
    q: "How do I increase brand awareness for my small business?",
    a: "Start with a strong visual identity — logo, colours, fonts — then show up consistently where your customers already are: Google Business Profile, Facebook, Instagram and WhatsApp Business. Use professional posters, a simple website and branded company profiles. Reply fast, keep your messaging clear and make every interaction look polished. BrandIn Studio helps small businesses build all of these assets so your brand gets recognised and remembered.",
  },
  {
    q: "Why do small businesses need professional branding?",
    a: "Because first impressions decide whether a customer contacts you or scrolls past. Professional branding gives small businesses the same credibility as bigger competitors. It improves tender applications, makes referrals easier and turns casual viewers into paying customers. In South Africa, where trust and word-of-mouth matter deeply, a professional brand helps you win bigger opportunities and grow faster.",
  },
  {
    q: "What is a branding review?",
    a: "A branding review is a professional assessment of how your business looks, sounds and presents itself to customers. At BrandIn Studio we review your logo, colours, fonts, company profile, business cards, letterhead, social media presence, website and overall brand consistency. The goal is to identify what looks outdated, unclear or unprofessional, then refine it so your business looks more trusted and market-ready.",
  },
  {
    q: "What are the best sites for brand reviews?",
    a: "The best platforms to review and improve your brand presence are Google Business Profile, Facebook, Instagram, LinkedIn, your website, WhatsApp Business, HelloPeter and online business directories. These show how customers see your business online. BrandIn Studio helps you improve your brand appearance across all of these platforms so you look professional, credible and easy to contact.",
  },
  {
    q: "Is branding worth it?",
    a: "Yes. Customers often judge a business before they even speak to you. A strong brand helps you look professional, trustworthy and ready for bigger opportunities. Good branding improves customer confidence, supports better marketing, lets you charge properly and makes your business stand out from competitors.",
  },
  {
    q: "What are the top 5 brands?",
    a: "The world's strongest brands — Apple, Nike, Coca-Cola, Samsung and Toyota — are known for clear identity, trust, consistency and a strong customer experience. The lesson for small businesses is simple: your brand must be memorable, consistent and easy to recognise across every platform. That is exactly what BrandIn Studio builds for you.",
  },
  {
    q: "How much does it cost to do branding?",
    a: "Branding costs depend on what your business needs. Our Starter Package is R999 and includes a custom logo with brand colours and fonts plus a company profile design. It is ideal for small businesses that want to look professional without paying agency-level prices.",
  },
  {
    q: "What are the 7 pillars of branding?",
    a: "The 7 pillars of branding are brand purpose, brand identity, target audience, brand positioning, visual design, brand voice and brand consistency. Together they help your business communicate clearly and build trust. BrandIn Studio helps small businesses align these pillars through professional design, profiles, stationery, websites and digital setup.",
  },
  {
    q: "What are the five disadvantages of branding?",
    a: "Branding becomes a disadvantage only when it is done badly. The main risks are unclear messaging, inconsistent design, poor-quality logos, outdated visuals and branding that doesn't match the target customer. A weak brand makes a business look unprofessional. BrandIn Studio focuses on clean, modern, practical branding that helps you look credible and ready to trade.",
  },
  {
    q: "What is included in the BrandIn Studio Starter Package?",
    a: "The R999 Starter Package includes a custom logo with brand colours and fonts, plus a company profile design. It is built for startups, small businesses, contractors, service providers and any company that needs professional branding quickly.",
  },
  {
    q: "Who needs branding services?",
    a: "Branding services are useful for small businesses, startups, contractors, consultants, cleaning companies, construction companies, transport companies, beauty brands, restaurants, mechanics, plumbers, electricians and any business that wants to look more professional and attract better customers.",
  },
  {
    q: "Why choose BrandIn Studio?",
    a: "BrandIn Studio creates practical, professional branding for businesses that want to look credible, win customers and present themselves properly online and offline. We focus on clean design, business-ready documents and brand consistency across Google, WhatsApp, Facebook, websites and printed materials — at prices small businesses can actually afford.",
  },
  {
    q: "How does the free brand assessment work?",
    a: "Submit the short form on this page and we'll message you on WhatsApp within one business day. We review your current logo, website, WhatsApp profile and social presence, then send you a short video or voice note with specific recommendations — completely free, no obligation.",
  },
  {
    q: "How long does a branding or website project take?",
    a: "Starter branding projects are typically delivered within 5–10 working days. A full website or brand identity package takes 2–4 weeks depending on scope and feedback rounds. We always agree on a clear timeline before we start.",
  },
];

function LandingPage() {
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const choosePackage = (name: string) => {
    setSelectedPackage(name);
    requestAnimationFrame(() => {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // Catch-all WhatsApp click tracking for plain <a href={waLink()}> anchors.
  // Only counts genuine user clicks (event.isTrusted) so programmatic opens
  // via openWhatsApp() — which already track themselves — don't double-fire.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!e.isTrusted) return;
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.("a") as HTMLAnchorElement | null;
      if (!anchor?.href) return;
      if (!/(wa\.me|whatsapp\.com)/i.test(anchor.href)) return;
      const source =
        anchor.dataset.waSource ||
        anchor.getAttribute("aria-label") ||
        anchor.textContent?.trim().slice(0, 60) ||
        "anchor";
      trackWhatsAppClick(source, { href: anchor.href });
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, []);
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-background font-sans text-foreground">
      <Header />
      <HeroBanner
        kicker={
          <>
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
            </span>
            {t("hero.kicker")}
          </>
        }
        title={t("hero.title")}
        titleHighlight={t("hero.titleHighlight")}
        subtitle={t("hero.subtitle")}
        primaryCta={{ label: t("cta.whatsappChat"), href: waLink(), external: true }}
        secondaryCta={{ label: t("cta.seeOurWork"), href: "#portfolio", variant: "secondary" }}
        images={[
          {
            src: heroDesignerVideo.url,
            alt: "Graphic designer creating logos and websites at BrandIn Studio",
            video: true,
            poster: heroSlide1,
          },
          { src: brandInABoxKit, alt: "BrandIn Studio Brand in a Box kit — logo, company profile, business cards, email signature" },
          { src: heroSlide1, alt: "BrandIn Studio matte-black branded delivery van" },
          { src: heroGazeboBmw.url, alt: "Branded gazebo, feather flags and pull-up banner — BMW activation setup" },
          { src: heroGazeboJetour.url, alt: "Branded gazebo, feather flag and pull-up banner — Jetour activation setup" },
          { src: heroSlide2, alt: "Brand identity stationery — business cards, letterhead, folder" },
          { src: heroSlide3, alt: "Roll-up banner and outdoor signage mockup" },
        ]}
        checks={[t("hero.check1"), t("hero.check2"), t("hero.check3")]}
      />
      <FeaturedOffer />
      <TrustBar />
      <AboutUs />
      <Problems />
      <Solution />
      <Services />
      <Packages onChoose={choosePackage} />
      <LeadForm selectedPackage={selectedPackage} />
      <Portfolio />
      <WhyUs />
      <Process />
      <Testimonials />
      <FAQ />
      <FinalCTA />
      <Footer />
      <StickyWhatsApp />
      <MobileLeadBar />
    </div>
  );
}

function FeaturedOffer() {
  return (
    <section className="relative bg-secondary/40 py-14 sm:py-20">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-6 lg:grid-cols-2 lg:gap-16">
        <div className="order-2 lg:order-1">
          <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-accent">Limited-Time Offer</span>
          <h2 className="mt-4 text-3xl leading-[1.05] tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Brand in a Box —<br /><span className="font-semibold">only R1,999</span>
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
            Make your business look credible, polished and ready for bigger opportunities.
            Everything you need to launch a professional brand — delivered in days.
          </p>
          <ul className="mt-6 grid gap-2.5 text-sm text-foreground sm:grid-cols-2">
            {["Logo Design / Refinement","Company Profile","Business Card Design","Letterhead Design","Email Signature"].map((i) => (
              <li key={i} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />{i}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a href={waLink("Hi BrandIn Studio, I want the Brand in a Box package (R1,999).")}
               target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-elegant transition hover:-translate-y-0.5">
              <MessageCircle className="h-4 w-4" /> Claim This Offer
            </a>
            <a href="#contact"
               className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-card px-7 py-3.5 text-sm font-semibold text-foreground transition hover:bg-primary hover:text-primary-foreground">
              Get a Free Assessment <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Was R2,999 · Save R1,000 · Includes everything above</p>
        </div>
        <div className="order-1 lg:order-2">
          <div className="relative mx-auto max-w-md">
            <div aria-hidden className="absolute -inset-2 rounded-3xl bg-accent/15 blur-2xl" />
            <img src={brandInABoxKit} alt="BrandIn Studio Brand in a Box — logo, profile, stationery and email signature kit" loading="lazy"
                 className="relative w-full rounded-2xl shadow-elegant ring-1 ring-border" />
          </div>
        </div>
      </div>
    </section>
  );
}

function MobileLeadBar() {
  const { t } = useI18n();
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 flex gap-2 border-t border-border bg-background/95 p-3 backdrop-blur-xl shadow-elegant sm:hidden">
      <a href="#contact"
         className="flex-1 inline-flex items-center justify-center rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-transform active:scale-[0.98]">
        {t("cta.freeAssessment")}
      </a>
      <a href={waLink()} target="_blank" rel="noopener noreferrer"
         className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-whatsapp px-4 py-3 text-sm font-semibold text-whatsapp-foreground transition-transform active:scale-[0.98]">
        <MessageCircle className="h-4 w-4" /> WhatsApp
      </a>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/65">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-5 py-3.5 sm:px-6 sm:py-4">
        <a href="#top" className="flex items-center gap-2.5" aria-label="BrandIn Studio — Home">
          <img src={brandLogo} alt="BrandIn Studio logo" width={260} height={65} className="h-12 w-auto sm:h-14" />
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {nav.map((n) => (
            <a key={n.href} href={n.href} className="relative text-sm font-medium text-muted-foreground transition-colors hover:text-foreground after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:w-0 after:bg-accent after:transition-all hover:after:w-full">
              {t(n.key)}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <a
            href={waLink()}
            target="_blank" rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground shadow-soft transition-all hover:-translate-y-0.5 hover:bg-primary hover:text-primary-foreground hover:shadow-elegant"
          >
            <MessageCircle className="h-4 w-4 text-whatsapp" /> {t("cta.whatsappUs")}
          </a>
          <button onClick={() => setOpen(!open)} className="md:hidden p-2 -mr-2 text-foreground" aria-label="Menu">
            <ChevronDown className={`h-6 w-6 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-border bg-background px-5 py-4 flex flex-col gap-2">
          {nav.map((n) => (
            <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="text-sm font-medium text-foreground py-2.5 border-b border-border last:border-0">{t(n.key)}</a>
          ))}
          <a href={waLink()} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp px-5 py-3.5 font-semibold text-whatsapp-foreground shadow-soft">
            <MessageCircle className="h-4 w-4" /> {t("cta.whatsappUs")}
          </a>
        </div>
      )}
    </header>
  );
}

function TrustBar() {
  const items = ["Energy", "Security", "Logistics", "Ministry", "Industrial", "Property", "Retail", "Beauty"];
  return (
    <div className="border-y border-border/70 bg-card">
      <div className="mx-auto max-w-7xl px-5 py-5 sm:px-6">
        <p className="text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Trusted across industries
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-7 gap-y-2 text-sm font-semibold tracking-tight text-foreground/70">
          {items.map((i) => <span key={i}>{i}</span>)}
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ kicker, title, sub }: { kicker?: string; title: string; sub?: string }) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      {kicker && <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-gold">{kicker}</span>}
      <h2 className="mt-3 text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-[2.75rem]">{title}</h2>
      {sub && <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">{sub}</p>}
    </div>
  );
}

function AboutUs() {
  const { t } = useI18n();
  return (
    <section id="about" className="bg-secondary py-16 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-4xl px-5 sm:px-6">
        <SectionHeader
          kicker={t("about.kicker")}
          title={t("about.title")}
        />
        <div className="mt-10 space-y-6 text-center">
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t("about.p1")}
          </p>
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t("about.p2")}
          </p>
        </div>
      </div>
    </section>
  );
}

function Problems() {
  const gridRef = useStaggerReveal<HTMLDivElement>(110);
  return (
    <section className="py-16 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <SectionHeader kicker="The Problem" title="Customers Judge Your Business in Seconds"
          sub="Most small businesses lose trust because their logo, posters, website and WhatsApp profile look inconsistent or amateur." />
        <div ref={gridRef} className="mt-12 grid gap-5 sm:gap-6 md:grid-cols-3">
          {problems.map((p) => (
            <div key={p.title} className="rounded-2xl border border-border bg-card p-7 shadow-soft transition-transform hover:-translate-y-1">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-foreground">
                <p.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-lg font-bold text-foreground">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Solution() {
  return (
    <section className="bg-primary py-16 text-primary-foreground sm:py-20 lg:py-28">
      <div className="mx-auto max-w-5xl px-5 text-center sm:px-6">
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-accent">The Solution</span>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-[2.75rem]">
          A Complete, <span className="text-accent">Sales-Ready</span> Brand — Without the Agency Price Tag
        </h2>
        <p className="mt-5 text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
          We design the assets that make your business look credible, communicate clearly,
          and turn interest into enquiries on WhatsApp.
        </p>
        <a href={waLink()} target="_blank" rel="noopener noreferrer"
           className="mt-8 inline-flex items-center gap-2 rounded-full bg-accent px-7 py-4 font-bold text-accent-foreground shadow-elegant transition hover:opacity-95">
          Get My Free Assessment <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </section>
  );
}

function Services() {
  const gridRef = useStaggerReveal<HTMLDivElement>(80);
  return (
    <section id="services" className="py-16 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <SectionHeader kicker="Services" title="Everything Your Brand Needs in One Studio"
          sub="From identity to lead generation — built for small businesses that want to look credible and convert." />
        <div ref={gridRef} className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s) => (
            <div key={s.title} className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-soft transition-all hover:-translate-y-1 hover:border-gold hover:shadow-elegant">
              <div className="aspect-[4/3] overflow-hidden bg-secondary">
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  width={800}
                  height={600}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex-1 p-5">
                <h3 className="text-base font-bold leading-snug text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Packages({ onChoose }: { onChoose: (name: string) => void }) {
  const handleBuy = (name: string) => {
    onChoose(name);
    openWhatsApp(
      `Hi BrandIn Studio, I'd like to get started with the ${name} package. Please send me the next steps.`,
      `package_card:${name}`
    );
  };
  const ccy = useCurrency();
  const showLocalNote = ccy.ready && ccy.code !== "ZAR";

  return (
    <section id="packages" className="bg-secondary py-16 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <SectionHeader kicker="Packages" title="Clear Pricing. Real Outcomes."
          sub="Choose the package that matches your stage. Every item is designed to move your business forward." />
        {showLocalNote && (
          <p className="mt-3 text-center text-xs text-muted-foreground">
            Prices shown in {ccy.code} (live rate from ZAR) for your region. Invoiced in ZAR.
          </p>
        )}
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {packages.map((p) => (
            <div key={p.name}
              className={`relative flex flex-col rounded-3xl border bg-card shadow-soft transition-transform hover:-translate-y-1 ${
                p.highlight ? "border-gold ring-2 ring-gold/40 shadow-elegant lg:scale-[1.04]" : "border-border"
              }`}>
              {p.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gold px-4 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-gold-foreground shadow-soft">
                  Most Popular
                </span>
              )}
              <div className="p-6 pb-4 sm:p-7 sm:pb-5 text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{p.name}</p>
                {p.isCustom ? (
                  <p className="mt-2 text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">Custom</p>
                ) : (
                  <p className="mt-2 text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                    {ccy.ready ? formatPrice(p.priceZar, ccy) : `R${p.priceZar.toLocaleString("en-ZA")}`}
                  </p>
                )}
                <p className="mt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {p.isCustom ? "Tailored quote" : `One-time fee${showLocalNote ? ` · ~R${p.priceZar.toLocaleString("en-ZA")}` : ""}`}
                </p>
                <p className="mt-3 text-sm font-semibold leading-snug text-gold">{p.outcome}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">{p.tagline}</p>
              </div>


              <div className="flex-1 border-t border-border px-6 pb-7 pt-5 sm:px-7">
                <ul className="space-y-3">
                  {p.bullets.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-whatsapp" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-border p-6 sm:p-7">
                <a
                  href={waLink(`Hi BrandIn Studio, I'm interested in the ${p.name} package. Please send me the next steps.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold transition ${
                    p.highlight
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  }`}>
                  <MessageCircle className="h-4 w-4" /> {p.cta}
                </a>
              </div>
            </div>
          ))}
        </div>
        <PackageComparison />
      </div>
    </section>
  );
}

function PackageComparison() {
  const ccy = useCurrency();
  return (
    <div className="mt-16 overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary">
            <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Feature</th>
            {packages.map((p) => (
              <th key={p.name} className="px-5 py-4 text-center">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-foreground">{p.name}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {p.isCustom ? "Custom" : ccy.ready ? formatPrice(p.priceZar, ccy) : `R${p.priceZar.toLocaleString("en-ZA")}`}
                </p>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-border bg-card">
            <td className="px-5 py-3.5 text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">Best for you if…</td>
            {packages.map((p) => (
              <td key={p.name} className="px-5 py-3.5 text-center text-xs font-semibold text-gold">{p.tagline}</td>
            ))}
          </tr>
          {comparisonFeatures.map((feature, i) => (
            <tr key={feature} className={i % 2 === 0 ? "bg-card" : "bg-secondary/40"}>
              <td className="px-5 py-3.5 font-medium text-foreground">{feature}</td>
              {packages.map((p) => {
                const val = comparisonMap[p.name]?.[i] ?? "dash";
                return (
                  <td key={p.name} className="px-5 py-3.5 text-center">
                    {val === "check" ? (
                      <CheckCircle2 className="mx-auto h-4 w-4 text-whatsapp" />
                    ) : val === "dash" ? (
                      <Minus className="mx-auto h-4 w-4 text-muted-foreground/40" />
                    ) : (
                      <span className="text-xs font-semibold text-gold">{val}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Portfolio() {
  const gridRef = useStaggerReveal<HTMLDivElement>(70);
  return (
    <section id="portfolio" className="py-16 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <SectionHeader kicker="Portfolio" title="Recent Work Built for Real Businesses"
          sub="A selection of branding, profile and identity projects delivered across industries." />
        <div ref={gridRef} className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {portfolio.map((item) => (
            <figure key={item.label} className="group overflow-hidden rounded-2xl border border-border bg-card shadow-soft transition-all hover:-translate-y-1 hover:shadow-elegant">
              <div className={`relative aspect-[4/5] overflow-hidden ${item.tone === "dark" ? "bg-card" : "bg-secondary"}`}>
                <img src={item.img} alt={item.label} loading="lazy"
                     className="absolute inset-0 h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-[1.04]" />
                <span className="absolute top-3 left-3 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-foreground shadow-soft">
                  {item.tag}
                </span>
              </div>
              <figcaption className="p-4">
                <p className="text-sm font-semibold leading-snug text-foreground">{item.label}</p>
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="mt-12 text-center">
          <a href={waLink("Hi BrandIn Studio, I'd like to discuss a project similar to your portfolio.")}
             target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-2 rounded-full bg-whatsapp px-7 py-3.5 font-semibold text-whatsapp-foreground shadow-soft transition hover:opacity-95">
            <MessageCircle className="h-4 w-4" /> Start Your Project on WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  return (
    <section className="bg-secondary py-16 sm:py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-6 lg:grid-cols-2 lg:items-center">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-gold">Why BrandIn Studio</span>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl lg:text-[2.75rem]">
            Built for Businesses That Want to <span className="text-gold">Sell</span>, Not Just Look Pretty
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Every asset we create has one job: help your business attract trust and turn it into enquiries on WhatsApp.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {why.map((w) => (
            <div key={w} className="flex items-start gap-3 rounded-xl border border-border bg-card p-5 shadow-soft">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-whatsapp" />
              <span className="text-sm font-medium text-foreground">{w}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  const gridRef = useStaggerReveal<HTMLDivElement>(90);
  return (
    <section id="process" className="py-16 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <SectionHeader kicker="Our Process" title="A Simple Path From Idea to Leads"
          sub="No confusing jargon — just a clear, proven sequence." />
        <div ref={gridRef} className="mt-12 grid gap-5 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((s) => (
            <div key={s.n} className="relative rounded-2xl border border-border bg-card p-7 shadow-soft">
              <span className="text-5xl font-black text-gold/30">{s.n}</span>
              <h3 className="mt-3 text-lg font-bold text-foreground">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const gridRef = useStaggerReveal<HTMLDivElement>(100);
  return (
    <section className="bg-primary py-16 text-primary-foreground sm:py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-accent">Testimonials</span>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-[2.75rem]">Trusted by Growing Businesses</h2>
        </div>
        <div ref={gridRef} className="mt-12 grid gap-6 md:grid-cols-3">
          {testimonials.map((t) => (
            <div key={t.name} className="rounded-2xl border border-primary-foreground/15 bg-primary-foreground/[0.06] p-7 backdrop-blur">
              <div className="flex gap-1 text-accent">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="mt-4 leading-relaxed text-primary-foreground/90">"{t.quote}"</p>
              <div className="mt-6">
                <p className="font-bold">{t.name}</p>
                <p className="text-sm text-primary-foreground/65">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const leadSchema = z.object({
  name: z.string().trim().min(2, "Please enter your name").max(80, "Name is too long"),
  business: z.string().trim().min(2, "Please enter your business name").max(120, "Too long"),
  phone: z
    .string()
    .trim()
    .min(7, "Please enter a valid phone number")
    .max(20, "Phone number is too long")
    .regex(/^[+\d\s()-]+$/, "Use digits, spaces, +, - or () only"),
  email: z.union([z.literal(""), z.string().trim().email("Invalid email address").max(160)]).optional(),
  message: z.string().trim().min(10, "Tell us a bit more (min 10 characters)").max(1000, "Message is too long"),
});

type LeadErrors = Partial<Record<"name" | "business" | "phone" | "email" | "message" | "services", string>>;

function LeadForm({ selectedPackage }: { selectedPackage: string | null }) {
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<LeadErrors>({});
  const allServices = services.map(s => s.title);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  useEffect(() => {
    if (selectedPackage && packageServiceDefaults[selectedPackage]) {
      setSelectedServices(packageServiceDefaults[selectedPackage]);
    }
  }, [selectedPackage]);

  const toggleService = (title: string) => {
    setSelectedServices(prev =>
      prev.includes(title) ? prev.filter(s => s !== title) : [...prev, title]
    );
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const raw = {
      name: String(f.get("name") ?? ""),
      business: String(f.get("business") ?? ""),
      phone: String(f.get("phone") ?? ""),
      email: String(f.get("email") ?? ""),
      message: String(f.get("message") ?? ""),
    };

    const parsed = leadSchema.safeParse(raw);
    const nextErrors: LeadErrors = {};
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof LeadErrors;
        if (key && !nextErrors[key]) nextErrors[key] = issue.message;
      }
    }
    if (selectedServices.length === 0) {
      nextErrors.services = "Please select at least one service";
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});

    const data = parsed.data!;
    const servicesList = selectedServices.join(", ");
    const text = [
      "Hi BrandIn Studio — I'd like a free brand assessment.",
      "",
      `Name: ${data.name}`,
      `Business: ${data.business}`,
      `Phone: ${data.phone}`,
      data.email ? `Email: ${data.email}` : null,
      selectedPackage ? `Package: ${selectedPackage}` : null,
      `Service Needed: ${servicesList}`,
      "",
      `Message: ${data.message}`,
    ].filter(Boolean).join("\n");

    trackLeadSubmit({
      package: selectedPackage ?? "none",
      services: selectedServices,
      services_count: selectedServices.length,
      has_email: Boolean(data.email),
    });
    openWhatsApp(text, "lead_form_submit");
    setSubmitted(true);
  };

  return (
    <section id="contact" className="bg-secondary py-16 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-3xl px-5 sm:px-6">
        <SectionHeader
          kicker="Free Brand Assessment · No Obligation"
          title={selectedPackage ? `Let's start your ${selectedPackage} brief` : "Tell us about your business"}
          sub={selectedPackage
            ? "We've pre-filled the services in your package. Adjust if needed, then we'll continue on WhatsApp within one business day."
            : "Share a few details and we'll send you tailored recommendations on WhatsApp — usually within one business day."}
        />
        <form onSubmit={onSubmit} noValidate className="mt-10 grid gap-5 rounded-3xl border border-border bg-card p-6 shadow-elegant sm:mt-12 sm:p-8">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Your Name" name="name" required error={errors.name} placeholder="e.g. Thabo Mokoena" />
            <Field label="Business Name" name="business" required error={errors.business} placeholder="e.g. Mokoena Logistics" />
            <Field label="WhatsApp / Phone" name="phone" type="tel" required error={errors.phone} placeholder="+27 75 000 0000" />
            <Field label="Email (optional)" name="email" type="email" error={errors.email} placeholder="you@business.co.za" />
          </div>
          <div>
            <span className="text-sm font-semibold text-foreground">Service Needed <span className="text-destructive">*</span></span>
            <p className="mt-1 text-xs text-muted-foreground">
              {selectedPackage
                ? `Pre-selected from the ${selectedPackage}. Tick or untick as needed.`
                : "Select one or more services we can help you with."}
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {allServices.map((title) => {
                const checked = selectedServices.includes(title);
                return (
                  <label
                    key={title}
                    className={`flex cursor-pointer items-start gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm transition ${
                      checked
                        ? "border-primary bg-primary/5 text-foreground"
                        : "border-input bg-background text-muted-foreground hover:border-primary/50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleService(title)}
                      className="mt-0.5 h-4 w-4 flex-shrink-0 accent-primary"
                    />
                    <span className="leading-snug">{title}</span>
                  </label>
                );
              })}
            </div>
            {errors.services && <p className="mt-2 text-xs font-medium text-destructive">{errors.services}</p>}
          </div>
          <Field
            label="What do you need help with?"
            name="message"
            as="textarea"
            required
            error={errors.message}
            placeholder="Tell us about your business, current branding and what you'd like to improve."
          />
          <button type="submit"
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp px-7 py-4 font-bold text-whatsapp-foreground shadow-elegant transition hover:opacity-95">
            <MessageCircle className="h-5 w-5" /> {submitted ? "Opening WhatsApp…" : "Send & Continue on WhatsApp"} <ArrowRight className="h-4 w-4" />
          </button>
          <p className="text-center text-xs text-muted-foreground">
            We'll never share your details. Replies usually within 1 business day.
          </p>
        </form>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", required, as, options, error, placeholder }: {
  label: string; name: string; type?: string; required?: boolean;
  as?: "textarea" | "select"; options?: string[]; error?: string; placeholder?: string;
}) {
  const base = `mt-1.5 w-full rounded-xl border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 ${
    error ? "border-destructive focus:ring-destructive/30" : "border-input focus:border-border focus:ring-primary/30"
  }`;
  return (
    <label className="block">
      <span className="text-sm font-semibold text-foreground">
        {label}{required && <span className="text-destructive"> *</span>}
      </span>
      {as === "textarea" ? (
        <textarea name={name} required={required} rows={4} placeholder={placeholder} className={base} maxLength={1000} />
      ) : as === "select" ? (
        <select name={name} required={required} className={base} defaultValue="">
          <option value="" disabled>Select a service</option>
          {options?.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={type} name={name} required={required} placeholder={placeholder} className={base} maxLength={type === "tel" ? 20 : 255} />
      )}
      {error && <span className="mt-1.5 block text-xs font-medium text-destructive">{error}</span>}
    </label>
  );
}

function FAQ() {
  return (
    <section id="faq" className="py-16 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-4xl px-5 sm:px-6">
        <SectionHeader
          kicker="FAQ"
          title="Frequently Asked Questions"
          sub="Everything you need to know about timelines, deliverables and working with BrandIn Studio."
        />
        <div className="mt-10 sm:mt-12 space-y-3">
          {faqs.map((f, i) => (
            <div
              key={i}
              className="rounded-2xl border border-border bg-card px-6 sm:px-8 py-5 shadow-soft"
            >
              <p className="text-left text-[0.95rem] font-semibold text-foreground sm:text-base">
                {f.q}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {f.a}
              </p>
            </div>
          ))}
        </div>

        {/* FAQ WhatsApp CTA */}
        <div className="mt-12 sm:mt-14 rounded-2xl border border-border bg-card p-8 sm:p-10 text-center shadow-soft">
          <h3 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
            Still have questions?
          </h3>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
            Get a free brand assessment tailored to your business. Tell us your name, business and what you need help with — we'll reply with clear next steps.
          </p>
          <a
            href={waLink("Hi BrandIn Studio! I'd like to request a free brand assessment. My name is [Your Name] and my business is [Business Name]. I'm interested in [service].")}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-whatsapp px-7 py-3.5 text-sm font-bold text-whatsapp-foreground shadow-elegant ring-4 ring-whatsapp/30 transition-all hover:scale-105 hover:ring-whatsapp/50 sm:px-8 sm:py-4 sm:text-base"
          >
            <MessageCircle className="h-5 w-5" />
            Request Free Brand Assessment
          </a>
          <p className="mt-3 text-xs text-muted-foreground/80">{WA_DISPLAY} · Replies within 1 business day</p>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  const { t } = useI18n();
  return (
    <section className="relative overflow-hidden bg-primary py-20 text-primary-foreground sm:py-24">
      <div aria-hidden className="absolute top-0 right-0 h-72 w-72 rounded-full bg-accent/30 blur-3xl" />
      <div aria-hidden className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-whatsapp/25 blur-3xl" />
      <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-6">
        <h2 className="text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
          {t("final.title1")} <span className="text-accent">{t("final.titleAccent")}</span>?
        </h2>
        <p className="mt-5 text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
          {t("final.subtitle")}
        </p>
        <a href={waLink()} target="_blank" rel="noopener noreferrer"
           className="mt-9 inline-flex items-center gap-2 rounded-full bg-whatsapp px-8 py-4 text-base font-bold text-whatsapp-foreground shadow-elegant ring-4 ring-whatsapp/30 transition-all hover:-translate-y-0.5 hover:scale-[1.03] hover:ring-whatsapp/50">
          <MessageCircle className="h-5 w-5" /> {t("cta.whatsappChatWithUs")}
        </a>
        <p className="mt-4 text-sm text-primary-foreground/75">{WA_DISPLAY} · {t("final.replies")}</p>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useI18n();
  return (
    <footer className="border-t border-border bg-card py-14">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-6 md:grid-cols-3">
        <div>
          <img src={brandLogo} alt="BrandIn Studio logo" width={280} height={70} className="h-14 w-auto" />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">
            Professional branding, logo design and digital presence for small businesses across South Africa that want to look credible, visible, and ready to sell.
          </p>
        </div>
        <div>
          <h4 className="font-bold text-foreground">Contact</h4>
          <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
            <li className="flex gap-2"><MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /><span>Waterfall Office Park, Bekker Street, Vorna Valley, Midrand, 1686</span></li>
            <li className="flex gap-2"><MessageCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /><a href={waLink()} target="_blank" rel="noopener noreferrer" className="hover:text-foreground">WhatsApp {WA_DISPLAY}</a></li>
            <li className="flex gap-2"><Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /><a href="mailto:brandinstudio@outlook.com" className="hover:text-foreground">brandinstudio@outlook.com</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold text-foreground">Quick Links</h4>
          <ul className="mt-4 space-y-2 text-sm">
            {nav.map(n => <li key={n.href}><a href={n.href} className="text-muted-foreground transition-colors hover:text-foreground">{t(n.key)}</a></li>)}
          </ul>
        </div>
      </div>
      <div className="mx-auto mt-10 flex max-w-7xl flex-col justify-between gap-2 border-t border-border px-5 pt-6 text-xs text-muted-foreground sm:flex-row sm:px-6">
        <p>© {new Date().getFullYear()} BrandIn Studio. All rights reserved.</p>
        <p>Built for businesses that want to grow.</p>
      </div>
    </footer>
  );
}
